/**
 * src/components/shared/TaskTimeLogSection.jsx — Time logging for TaskDetailPanel
 * AC Nautitech Executive Assistant
 *
 * Log Time toggle, TimeLogForm, and time log history list.
 * Extracted from TaskDetailPanel.jsx to reduce file size.
 */

import { useState, useMemo } from 'react';
import { Clock } from 'lucide-react';
import TimeLogForm from './TimeLogForm';

const glassBox = {
  background: 'var(--glass)', borderRadius: 10, padding: 12, border: '1px solid var(--bdr)',
};

export default function TaskTimeLogSection({
  task,
  projectId,
  assigneeId,
  workTypes,
  timeLogs,
  peopleLookup,
  onCreateTimeEntry,
}) {
  const [showTimeLog, setShowTimeLog] = useState(false);

  const taskLogs = useMemo(() =>
    (timeLogs || [])
      .filter(tl => tl.task_id === task.id)
      .sort((a, b) => (b.date || '').localeCompare(a.date || '')),
    [timeLogs, task.id]
  );

  return (
    <>
      {/* Log Time toggle */}
      <button onClick={() => setShowTimeLog(v => !v)} style={{
        padding: 12, borderRadius: 14, fontSize: 14, fontWeight: 600, minHeight: 44,
        background: showTimeLog ? 'var(--navy-surface)' : 'var(--glass)',
        color: showTimeLog ? 'white' : 'var(--accent)',
        border: '1px solid var(--bdr)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, cursor: 'pointer',
      }}>
        <Clock size={16} /> Log Time
      </button>

      {/* Inline Time Log form */}
      {showTimeLog && (
        <div style={{ ...glassBox }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--t1)', marginBottom: 8 }}>
            Log Time Entry
          </div>
          <TimeLogForm
            taskId={task.id}
            projectId={projectId}
            assigneeId={assigneeId}
            workTypes={workTypes}
            compact
            onSubmit={onCreateTimeEntry}
            onCancel={() => setShowTimeLog(false)}
          />
        </div>
      )}

      {/* Time Log History */}
      {taskLogs.length > 0 && (
        <div style={{ ...glassBox, marginTop: 4 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--t1)', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
            <Clock size={14} /> Time Log ({taskLogs.reduce((s, tl) => s + (tl.duration_hours || 0), 0).toFixed(1)}h total)
          </div>
          {taskLogs.map(tl => {
            const tf = tl || {};
            const personId = tf.person_id;
            const personName = personId ? (peopleLookup[personId] || 'Unknown') : '';
            return (
              <div key={tl.id} style={{ padding: '8px 0', borderBottom: '1px solid var(--bdr)', fontSize: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
                  <span style={{ fontWeight: 600, color: 'var(--t1)' }}>{tf.duration_hours || 0}h — {tf.work_type || 'General'}</span>
                  <span style={{ color: 'var(--tm)', fontSize: 11 }}>{tf.date || ''}</span>
                </div>
                {tf.notes && <div style={{ color: 'var(--tm)', fontSize: 11 }}>{tf.notes}</div>}
                {personName && <div style={{ color: 'var(--tm)', fontSize: 10, marginTop: 2 }}>{personName}</div>}
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}
